"use client"

import { useState, useEffect, useRef } from "react"
import styles from "./Calendar.module.css"
import { Helmet } from "react-helmet-async"

const SchoolCalendar = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const sliderRef = useRef(null)
  const autoPlayRef = useRef(null)

  const months = [
    { name: "June", image: "/imgcalender/june.png", alt: "June 2025 Calendar" },
    { name: "July", image: "/imgcalender/july.png", alt: "July 2025 Calendar" },
    {
      name: "August",
      image: "/imgcalender/august.png",
      alt: "August 2025 Calendar",
    },
    {
      name: "September",
      image: "/imgcalender/september.png",
      alt: "September 2025 Calendar",
    },
    {
      name: "October",
      image: "/imgcalender/october.png",
      alt: "October 2025 Calendar",
    },
    {
      name: "November",
      image: "/imgcalender/november.png",
      alt: "November 2025 Calendar",
      alt: "November 2025 Calendar",
    },
    {
      name: "December",
      image: "/imgcalender/december.png",
      alt: "December 2025 Calendar",
    },
  ]

  const getCurrentMonthIndex = () => {
    const currentMonth = new Date().getMonth() // 0-11 (Jan=0, Dec=11)
    const academicStartMonth = 5 // June = 5

    if (currentMonth >= academicStartMonth) {
      // June to December of current year
      return currentMonth - academicStartMonth
    } else {
      // January to May of next year (not in our range, default to June)
      return 0
    }
  }

  const currentMonthIndex = getCurrentMonthIndex()

  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % months.length)
      }, 4000)
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current)
      }
    }
  }, [isAutoPlaying, months.length])

  useEffect(() => {
    setCurrentSlide(currentMonthIndex)
  }, [currentMonthIndex])

  const goToSlide = (index) => {
    setCurrentSlide(index)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 5000)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % months.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 5000)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + months.length) % months.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 5000)
  }

  return (
    <>
      <Helmet>
        <title>Hillgreen School Calendar</title>
        <meta name="description" content="Hillgreen School Calendar - View all months and important school events" />
        <meta name="keywords" content="Hillgreen, School, Calendar, Events, Academic Year" />
        <link rel="canonical" href="https://www.hillgreenhighschool.in/calendar" />
        <meta property="og:title" content="School Calendar" />
        <meta
          property="og:description"
          content="Hillgreen School Calendar - View all months and important school events"
        />
      </Helmet>

      <div className={styles.calendar}>
        <div className={styles.calendar_head}>
          <h1>School Calendar</h1>
          <p className={styles.subtitle}>Academic Year 2025</p>
        </div>

        <div className={styles.colorIndicators}>
          <img
            src="/imgcalender/head.png"
            alt="Color Indicators - Class Activity, Vacation, Holidays, Event, Evaluation for Pre-Primary"
            className={styles.indicatorsImage}
          />
        </div>

        <div className={styles.calendar_main}>
          <div className={styles.calendar_container}>
            <div className={styles.sliderContainer}>
              <div className={styles.currentMonthBadge}>
                <span className={styles.currentMonthText}>
                  {months[currentSlide].name} 2025
                  {currentSlide === currentMonthIndex && <span className={styles.currentIndicator}>Current Month</span>}
                </span>
              </div>

              <div className={styles.slider} ref={sliderRef}>
                <div
                  className={styles.sliderTrack}
                  style={{ transform: `translateX(-${currentSlide * (100 / months.length)}%)` }}
                >
                  {months.map((month, index) => (
                    <div key={index} className={styles.slide}>
                      <img src={month.image || "/placeholder.svg"} alt={month.alt} className={styles.calendarImage} />
                    </div>
                  ))}
                </div>

                <button
                  className={`${styles.navButton} ${styles.prevButton}`}
                  onClick={prevSlide}
                  aria-label="Previous month"
                >
                  &#8249;
                </button>
                <button
                  className={`${styles.navButton} ${styles.nextButton}`}
                  onClick={nextSlide}
                  aria-label="Next month"
                >
                  &#8250;
                </button>
              </div>

              <div className={styles.monthNavigation}>
                {months.map((month, index) => (
                  <button
                    key={index}
                    className={`${styles.monthDot} ${index === currentSlide ? styles.activeDot : ""} ${index === currentMonthIndex ? styles.currentMonthDot : ""}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to ${month.name}`}
                  >
                    <span className={styles.monthName}>{month.name.slice(0, 3)}</span>
                  </button>
                ))}
              </div>

              <div className={styles.playControls}>
                <button
                  className={styles.playButton}
                  onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                  aria-label={isAutoPlaying ? "Pause slideshow" : "Play slideshow"}
                >
                  {isAutoPlaying ? "⏸️" : "▶️"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.quickNavigation}>
          <h3>Quick Navigation</h3>
          <div className={styles.quickNavWrapper}>
            <div className={styles.monthGrid}>
              {months.map((month, index) => (
                <button
                  key={index}
                  className={`${styles.monthButton} ${index === currentSlide ? styles.activeMonth : ""} ${index === currentMonthIndex ? styles.currentMonth : ""}`}
                  onClick={() => goToSlide(index)}
                >
                  {month.name}
                  {index === currentMonthIndex && <span className={styles.currentLabel}>Current</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SchoolCalendar
